import { useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { isTypingTarget, revealElement } from '../../layouts/softKeyboard';
import { Icon } from './Icon';
import { cx } from './primitives/cx';

/**
 * P-079 · La sección progresiva: el patrón «completar → validar → verde → encoger → seguir» que nació en Capturar
 * ticket (`doneRow`), formalizado como UNA pieza para todas las capturas guiadas. Cuatro estados visibles
 * (`data-state`), derivados de lo que el flujo le dice (Codex R1 · K13: validez, expansión y bloqueo por separado):
 *
 *   PENDIENTE (`pending`)   no está completa: abierta, con su pregunta y sus respuestas.
 *   COMPLETA  (`complete`)  validación local correcta y no está abierta: una fila con la etiqueta, el resumen, la
 *                           palomita y «Cambiar <etiqueta>». Verde = completo y VÁLIDO, no guardado: aquí nunca
 *                           se dice «Guardado»; la persistencia la dicen la acción primaria y sus avisos.
 *   ERROR     (`error`)     tiene un mensaje de error: abierta, no se encoge, y el mensaje queda asociado al control.
 *   EDITANDO  (`editing`)   completa y reabierta con «Cambiar»: abierta con sus valores (reabrir no borra nada).
 *
 * La pieza no decide cuándo avanzar, encogerse ni persistir: `complete` es la validación REAL del flujo (las
 * restricciones existentes: finito, mínimos, mayor que cero…; no «lleno»), `open` lo pone el flujo (al elegir,
 * al salir del campo con valor, al avanzar; nunca mientras se escribe) y `busy` congela el estado que tenía
 * mientras hay una confirmación o un guardado en curso (ni se encoge ni se reabre: Codex R2 · R3). Al encogerse,
 * si el foco cayó en `body` (el campo se desmontó), pasa a la siguiente sección abierta —a su contenedor, no a su
 * campo, para no levantar el teclado— y solo se desplaza si no se ve; al reabrirse con «Cambiar», el foco pasa al
 * contenido reabierto (Codex R2 · R1); si el foco ya está en otro control vivo (Cancelar, otro campo), se respeta
 * (Codex R1 · K12). Nada de esto ocurre al montar.
 *
 * «Cambiar» es una acción que sustituye la fila por el contenido (o salta a otro paso), no un acordeón: no lleva
 * `aria-expanded` ni `aria-controls` (Codex R2 · R6); el foco se administra aquí. Sin `children` es solo la fila de
 * resumen (las listas de revisión); `tone` dice si el dato está completo, pendiente (ámbar, «Llenar») o vacío y
 * opcional (gris).
 */
export type SectionTone = 'done' | 'missing' | 'empty';
export type SectionState = 'pending' | 'complete' | 'error' | 'editing';

export interface ProgressiveSectionProps {
  /** Lo que se contesta aquí («Proveedor», «Fecha y hora»). Da nombre a «Cambiar <etiqueta>». */
  label: string;
  /** El resumen compacto del valor, cuando está completa. */
  summary?: ReactNode;
  /** Validación local correcta (las restricciones existentes del flujo), no «lleno». */
  complete: boolean;
  /** Abierta (lo decide el flujo). Sin `children` no aplica. */
  open?: boolean;
  /** Reabrir para editar, sin perder nada. Sin esto no hay «Cambiar». */
  onOpen?: () => void;
  /** Error de la sección: abierta con el mensaje (`role="alert"`), asociado al control. */
  error?: ReactNode;
  /** El `id` del control al que corresponde el error; sin él, el primer control de la sección (Codex R2 · R4). */
  errorControl?: string;
  /** Mientras hay una confirmación o un guardado en curso: se queda como está y «Cambiar» no responde. */
  busy?: boolean;
  /** Solo para la fila de resumen de una revisión: completo (verde), pendiente (ámbar, «Llenar») o vacío (gris). */
  tone?: SectionTone;
  /** El texto del botón: «Cambiar» por omisión; «Llenar» para un pendiente. */
  changeLabel?: string;
  children?: ReactNode;
  className?: string;
}

const OPEN_SECTIONS = '.n3-section[data-state="pending"], .n3-section[data-state="editing"], .n3-section[data-state="error"]';
const CONTROLS = 'input:not([type="hidden"]), select, textarea';

/** El shell, si está montado: para desplazar la página (y no la hoja) cuando toca. */
const shellScroller = () => document.querySelector<HTMLElement>('.n3-shell__scroll');
const focusLost = () => { const active = document.activeElement; return !active || active === document.body || active === document.documentElement; };

export function ProgressiveSection({ label, summary, complete, open = false, onOpen, error, errorControl, busy = false, tone, changeLabel, children, className }: ProgressiveSectionProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const rootRef = useRef<HTMLDivElement>(null);
  const hasError = error !== undefined && error !== null && error !== false && error !== '';
  // Nunca mientras se escribe: con un campo de escribir de la sección con el foco, la sección no se encoge aunque el
  // flujo la dé por cerrada y ya sea válida (el primer dígito válido la encogería a media captura). Al salir del campo
  // se vuelve a evaluar. Elegir una opción con un botón sí encoge en el acto: el botón no es un campo de escribir.
  const [typing, setTyping] = useState(false);
  const onFocusCapture = (event: FocusEvent<HTMLDivElement>) => { if (isTypingTarget(event.target)) setTyping(true); };
  const onBlurCapture = (event: FocusEvent<HTMLDivElement>) => { if (isTypingTarget(event.target)) setTyping(false); };
  // Se encoge si está completa y cerrada; también cerrada y marcada como pendiente o vacía (`tone`): así un asistente
  // con un solo paso abierto deja los demás a la vista en ámbar con «Llenar» (las presentaciones). Con `busy` se
  // congela lo que había: ni se encoge una abierta ni se abre una encogida (Codex R2 · R3).
  const natural = !hasError && !open && !typing && (complete || tone === 'missing' || tone === 'empty');
  const frozen = useRef<boolean | null>(null);
  const collapsed = busy && frozen.current !== null ? frozen.current : natural;
  if (!busy) frozen.current = collapsed;
  const state: SectionState = hasError ? 'error' : collapsed ? 'complete' : complete ? 'editing' : 'pending';
  const summaryTone: SectionTone = complete ? 'done' : tone ?? 'done';
  const buttonLabel = changeLabel ?? (summaryTone === 'missing' ? 'Llenar' : 'Cambiar');

  // El foco, en los dos sentidos. Al encogerse (abierta → completa) el campo que tenía el foco desaparece: si el foco
  // cayó en body, pasa a la siguiente sección abierta, o a «Cambiar» de esta. Al reabrirse (completa → abierta) el
  // botón «Cambiar» desaparece: si el foco cayó en body, pasa al contenido reabierto (su contenedor, no su campo,
  // para no levantar el teclado; si un campo ya lo tomó con autoFocus, se respeta). Nunca al montar; nunca si el
  // foco ya está en otro sitio vivo.
  const wasCollapsed = useRef<boolean | null>(null);
  useLayoutEffect(() => {
    const before = wasCollapsed.current;
    wasCollapsed.current = collapsed;
    if (before === null || before === collapsed || !focusLost()) return;
    const root = rootRef.current;
    if (!root) return;
    if (collapsed) {
      const next = Array.from(document.querySelectorAll<HTMLElement>(OPEN_SECTIONS)).find((candidate) => Boolean(root.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING));
      const target = next ?? root.querySelector<HTMLElement>('.n3-section__change');
      if (!target) return;
      target.focus({ preventScroll: true });
      if (next) revealElement(next, { shellScroller: shellScroller() });
    } else {
      root.focus({ preventScroll: true });
      revealElement(root, { shellScroller: shellScroller() });
    }
  }, [collapsed]);

  // El error se anuncia (`role="alert"`), queda asociado al control que corresponde (Codex R1 · K15, R2 · R4) y, si
  // apareció mientras se escribía con el teclado arriba, se trae a la vista junto con su campo (Codex R2 · R8).
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !hasError) return undefined;
    const control = (errorControl ? document.getElementById(errorControl) : null) ?? root.querySelector<HTMLElement>(CONTROLS);
    if (!control) return undefined;
    const described = control.getAttribute('aria-describedby');
    const invalid = control.getAttribute('aria-invalid');
    control.setAttribute('aria-describedby', [described, errorId].filter(Boolean).join(' '));
    control.setAttribute('aria-invalid', 'true');
    const message = document.getElementById(errorId);
    revealElement(control, { shellScroller: shellScroller(), companion: message });
    return () => {
      if (!control.isConnected) return;
      if (described) control.setAttribute('aria-describedby', described); else control.removeAttribute('aria-describedby');
      if (invalid) control.setAttribute('aria-invalid', invalid); else control.removeAttribute('aria-invalid');
    };
  }, [hasError, errorId, errorControl]);

  if (collapsed) {
    return (
      <div className={cx('n3-section', `n3-section--${summaryTone === 'done' ? 'complete' : summaryTone}`, className)} data-state={summaryTone === 'done' ? 'complete' : summaryTone} ref={rootRef}>
        <span aria-hidden="true" className="n3-section__mark"><Icon name={summaryTone === 'missing' ? 'warning' : 'check'} size={14} /></span>
        <div className="n3-section__text">
          <small className="n3-section__label">{label}</small>
          <strong className="n3-section__value">{summary}</strong>
          <span className="n3-visually-hidden">{summaryTone === 'done' ? ' (completo)' : summaryTone === 'missing' ? ' (pendiente)' : ' (sin respuesta)'}</span>
        </div>
        {onOpen && (
          <button aria-label={`${buttonLabel} ${label}`} className="n3-section__change" disabled={busy} onClick={onOpen} type="button">
            {buttonLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cx('n3-section', `n3-section--${state}`, className)} data-state={state} onBlurCapture={onBlurCapture} onFocusCapture={onFocusCapture} ref={rootRef} tabIndex={-1}>
      {children}
      {hasError && (
        <p className="n3-section__error" id={errorId} role="alert">
          <Icon name="warning" size={16} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
